import { defineStore } from 'pinia';
import {
    collection,
    deleteDoc,
    doc,
    limit,
    onSnapshot,
    orderBy,
    query,
    setDoc,
    updateDoc,
    writeBatch,
} from 'firebase/firestore';
import {
    buildDefaultNotificationPreferences,
    clientNotificationTab,
    isNotificationDismissed,
    isNotificationUnread,
    matchesNotificationContext,
    matchesNotificationMute,
    normalizeNotificationPreferences,
    notificationClientMuteId,
    notificationEntityMuteId,
    notificationPageKey,
    projectNotificationTab,
} from '~/lib/notifications';
import type { ClientNotificationTab, NotificationPageKey, ProjectNotificationTab } from '~/lib/notifications';
import { isProjectInactive } from '~/lib/projectLifecycle';
import type {
    AppNotification,
    NotificationEntityType,
    NotificationMute,
    NotificationSurface,
    UserNotificationPreferences,
} from '~/types/models';

export type NotificationContext = {
    surface: NotificationSurface;
    tenantId?: string | null;
    clientId?: string | null;
    projectId?: string | null;
};

function sortByCreatedAtDesc(items: AppNotification[]) {
    return [...items].sort((a, b) => {
        const left = toMillis(a.createdAt);
        const right = toMillis(b.createdAt);
        return right - left;
    });
}

function toMillis(value: unknown): number {
    if (!value) return 0;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
        return (value as { toDate: () => Date }).toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
        return Number((value as { seconds: number }).seconds) * 1000;
    }

    const parsed = new Date(String(value)).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

export const useNotificationStore = defineStore('notifications', {
    state: () => ({
        items: [] as AppNotification[],
        mutes: [] as NotificationMute[],
        loading: false,
        error: null as string | null,
        startedForUserId: null as string | null,
        unsubscribeNotifications: null as null | (() => void),
        unsubscribeMutes: null as null | (() => void),
    }),

    getters: {
        unreadCount: (state) => {
            const inactiveProjectIds = new Set(useProjectStore().projects
                .filter(project => isProjectInactive(project.status))
                .map(project => project.id));
            return state.items.filter((notification) => (
                (!notification.projectId || !inactiveProjectIds.has(notification.projectId))
                && isNotificationUnread(notification)
                && (!Array.isArray(notification.channels) || notification.channels.includes('in_app'))
                && !state.mutes.some((mute) => matchesNotificationMute(notification, mute))
            )).length;
        },
    },

    actions: {
        _notificationCollectionPath(userId: string) {
            return `users/${userId}/notifications`;
        },

        _muteCollectionPath(userId: string) {
            return `users/${userId}/notificationMutes`;
        },

        _currentUserId() {
            const { $auth } = useNuxtApp();
            return $auth.currentUser?.uid || useAuthStore().user?.uid || useUserStore().user?.id || '';
        },

        stopRealtime() {
            this.unsubscribeNotifications?.();
            this.unsubscribeMutes?.();
            this.unsubscribeNotifications = null;
            this.unsubscribeMutes = null;
        },

        startRealtime(maxResults = 100) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();

            if (!userId) {
                this.stopRealtime();
                this.items = [];
                this.mutes = [];
                this.startedForUserId = null;
                return;
            }

            if (!$db) {
                this.stopRealtime();
                this.loading = false;
                this.error = 'Notification storage is unavailable.';
                return;
            }

            if (this.unsubscribeNotifications && this.unsubscribeMutes && this.startedForUserId === userId) {
                return;
            }

            this.stopRealtime();
            this.loading = true;
            this.error = null;
            this.startedForUserId = userId;

            const notificationsRef = collection($db, this._notificationCollectionPath(userId));
            const notificationQuery = query(notificationsRef, orderBy('createdAt', 'desc'), limit(maxResults));

            this.unsubscribeNotifications = onSnapshot(notificationQuery, (snapshot) => {
                this.items = sortByCreatedAtDesc(snapshot.docs.map((entry) => ({
                    id: entry.id,
                    ...entry.data(),
                } as AppNotification)));
                this.loading = false;
            }, (error) => {
                this.error = error.message || 'Unable to load notifications.';
                this.loading = false;
            });

            const mutesRef = collection($db, this._muteCollectionPath(userId));
            this.unsubscribeMutes = onSnapshot(mutesRef, (snapshot) => {
                this.mutes = snapshot.docs.map((entry) => ({
                    id: entry.id,
                    ...entry.data(),
                } as NotificationMute));
            }, (error) => {
                this.error = error.message || 'Unable to load notification mutes.';
            });
        },

        visibleNotifications(context: NotificationContext) {
            const inactiveProjectIds = new Set(useProjectStore().projects
                .filter(project => isProjectInactive(project.status))
                .map(project => project.id));
            return this.items.filter((notification) => {
                if (notification.projectId && inactiveProjectIds.has(notification.projectId)) return false;
                if (Array.isArray(notification.channels) && !notification.channels.includes('in_app')) {
                    return false;
                }

                if (isNotificationDismissed(notification)) return false;
                if (this.mutes.some((mute) => matchesNotificationMute(notification, mute))) return false;
                return matchesNotificationContext(notification, context);
            });
        },

        unreadCountForContext(context: NotificationContext) {
            return this.visibleNotifications(context).filter(isNotificationUnread).length;
        },

        unreadCountForPage(page: NotificationPageKey, context: NotificationContext) {
            return this.visibleNotifications(context)
                .filter(isNotificationUnread)
                .filter((notification) => notificationPageKey(notification) === page)
                .length;
        },

        unreadCountForClientTab(tab: ClientNotificationTab, context: NotificationContext) {
            return this.visibleNotifications(context)
                .filter(isNotificationUnread)
                .filter((notification) => clientNotificationTab(notification) === tab)
                .length;
        },

        unreadCountForProjectTab(tab: ProjectNotificationTab, context: NotificationContext) {
            return this.visibleNotifications(context)
                .filter(isNotificationUnread)
                .filter((notification) => projectNotificationTab(notification) === tab)
                .length;
        },

        async markAsRead(notificationId: string) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();
            if (!$db || !userId) return;

            const existing = this.items.find((item) => item.id === notificationId);
            if (!existing || existing.readAt) return;

            const readAt = new Date();
            await updateDoc(doc($db, this._notificationCollectionPath(userId), notificationId), {
                readAt,
            });
            this.items = this.items.map((item) => item.id === notificationId ? { ...item, readAt } : item);
        },

        async markAllAsRead(context: NotificationContext) {
            const userId = this._currentUserId();
            if (!userId) return;

            await this.markMatchingAsRead(context);
        },

        async markMatchingAsRead(context: NotificationContext, predicate?: (notification: AppNotification) => boolean) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();
            if (!$db || !userId) return;

            const unread = this.visibleNotifications(context)
                .filter(isNotificationUnread)
                .filter((notification) => predicate?.(notification) ?? true);
            if (!unread.length) return;

            const batch = writeBatch($db);
            const readAt = new Date();
            unread.forEach((item) => batch.update(doc($db, this._notificationCollectionPath(userId), item.id), { readAt }));
            await batch.commit();
            const unreadIds = new Set(unread.map((item) => item.id));
            this.items = this.items.map((item) => unreadIds.has(item.id) ? { ...item, readAt } : item);
        },

        async markEntityAsRead(entityType: NotificationEntityType, entityId: string, context: NotificationContext) {
            await this.markMatchingAsRead(context, (notification) => (
                notification.entityType === entityType && notification.entityId === entityId
            ));
        },

        async markEntitiesAsRead(entityType: NotificationEntityType, entityIds: string[], context: NotificationContext) {
            const entityIdSet = new Set(entityIds);
            await this.markMatchingAsRead(context, (notification) => (
                notification.entityType === entityType
                && Boolean(notification.entityId)
                && entityIdSet.has(notification.entityId!)
            ));
        },

        async markClientTabAsRead(tab: ClientNotificationTab, context: NotificationContext) {
            await this.markMatchingAsRead(context, (notification) => clientNotificationTab(notification) === tab);
        },

        async markProjectTabAsRead(tab: ProjectNotificationTab, context: NotificationContext) {
            await this.markMatchingAsRead(context, (notification) => projectNotificationTab(notification) === tab);
        },

        async dismissNotification(notificationId: string) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();
            if (!$db || !userId) return;

            const existing = this.items.find((item) => item.id === notificationId);
            if (!existing || existing.dismissedAt) return;

            const dismissedAt = new Date();
            await updateDoc(doc($db, this._notificationCollectionPath(userId), notificationId), {
                dismissedAt,
                readAt: existing.readAt || dismissedAt,
            });
            this.items = this.items.map((item) => item.id === notificationId
                ? { ...item, dismissedAt, readAt: item.readAt || dismissedAt }
                : item);
        },

        async dismissAll(context: NotificationContext) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();
            if (!$db || !userId) return;

            const visible = this.visibleNotifications(context);
            if (!visible.length) return;

            const batch = writeBatch($db);
            const dismissedAt = new Date();
            visible.forEach((item) => batch.update(doc($db, this._notificationCollectionPath(userId), item.id), {
                dismissedAt,
                readAt: item.readAt || dismissedAt,
            }));
            await batch.commit();
            const visibleIds = new Set(visible.map((item) => item.id));
            this.items = this.items.map((item) => visibleIds.has(item.id)
                ? { ...item, dismissedAt, readAt: item.readAt || dismissedAt }
                : item);
        },

        isClientMuted(tenantId: string, clientId: string) {
            const muteId = notificationClientMuteId(tenantId, clientId);
            return this.mutes.some((mute) => mute.id === muteId);
        },

        isEntityMuted(tenantId: string, entityType: NotificationEntityType, entityId: string) {
            const muteId = notificationEntityMuteId(tenantId, entityType, entityId);
            return this.mutes.some((mute) => mute.id === muteId);
        },

        async setClientMuted(tenantId: string, clientId: string, muted: boolean, label?: string) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();
            if (!$db || !userId || !tenantId || !clientId) return;

            const muteId = notificationClientMuteId(tenantId, clientId);
            const muteRef = doc($db, this._muteCollectionPath(userId), muteId);
            if (!muted) {
                await deleteDoc(muteRef);
                this.mutes = this.mutes.filter((mute) => mute.id !== muteId);
                return;
            }

            const now = new Date();
            const persistedMute: Omit<NotificationMute, 'id'> = {
                tenantId,
                scope: 'client',
                clientId,
                ...(label ? { label } : {}),
                createdAt: now,
                updatedAt: now,
            };
            await setDoc(muteRef, persistedMute);
            this.mutes = [...this.mutes.filter((item) => item.id !== muteId), { id: muteId, ...persistedMute }];
        },

        async setEntityMuted(
            tenantId: string,
            entityType: NotificationEntityType,
            entityId: string,
            muted: boolean,
            context: { clientId?: string; projectId?: string; label?: string } = {},
        ) {
            const { $db } = useNuxtApp();
            const userId = this._currentUserId();
            if (!$db || !userId || !tenantId || !entityType || !entityId) return;

            const muteId = notificationEntityMuteId(tenantId, entityType, entityId);
            const muteRef = doc($db, this._muteCollectionPath(userId), muteId);
            if (!muted) {
                await deleteDoc(muteRef);
                this.mutes = this.mutes.filter((mute) => mute.id !== muteId);
                return;
            }

            const now = new Date();
            const persistedMute: Omit<NotificationMute, 'id'> = {
                tenantId,
                scope: 'entity',
                entityType,
                entityId,
                ...(context.clientId ? { clientId: context.clientId } : {}),
                ...(context.projectId ? { projectId: context.projectId } : {}),
                ...(context.label ? { label: context.label } : {}),
                createdAt: now,
                updatedAt: now,
            };
            await setDoc(muteRef, persistedMute);
            this.mutes = [...this.mutes.filter((item) => item.id !== muteId), { id: muteId, ...persistedMute }];
        },

        async createNotification(userId: string, notification: Omit<AppNotification, 'id' | 'createdAt'> & { createdAt?: unknown }) {
            const { $db } = useNuxtApp();
            if (!$db) throw new Error('Notification storage is unavailable.');
            const notificationsRef = collection($db, this._notificationCollectionPath(userId));
            const entryRef = doc(notificationsRef);

            await setDoc(entryRef, {
                ...notification,
                createdAt: notification.createdAt || new Date(),
            });
        },

        async updateNotificationPreferences(preferences: Partial<UserNotificationPreferences>) {
            const { $auth } = useNuxtApp();
            const userId = $auth.currentUser?.uid || useUserStore().user?.id;
            if (!userId) {
                throw new Error('You must be logged in to update notification preferences.');
            }

            const userStore = useUserStore();
            const nextPreferences = normalizeNotificationPreferences({
                ...buildDefaultNotificationPreferences(),
                ...(userStore.user?.preferences?.notifications || {}),
                ...preferences,
                updatedAt: new Date(),
            } as UserNotificationPreferences);

            const authUser = $auth.currentUser;
            if (!authUser) {
                throw new Error('You must be logged in to update notification preferences.');
            }

            const response = await $fetch<{ ok: boolean; preferences: UserNotificationPreferences }>('/api/notifications/preferences', {
                method: 'POST',
                headers: {
                    authorization: `Bearer ${await authUser.getIdToken()}`,
                },
                body: {
                    preferences: nextPreferences,
                },
            });
            const persistedPreferences = normalizeNotificationPreferences(response.preferences);

            console.info('[notifications] Preferences save response received', {
                userId,
                weeklyDigest: persistedPreferences.weeklyDigest !== false,
                eventCount: Object.keys(persistedPreferences.events || {}).length,
            });

            if (userStore.user?.id === userId) {
                userStore.user = {
                    ...userStore.user,
                    preferences: {
                        ...(userStore.user.preferences || {}),
                        notifications: persistedPreferences,
                    },
                    updatedAt: new Date(),
                };
            }

            await userStore.fetchUser(userId).catch((error) => {
                console.warn('[notifications] Unable to refresh user profile after saving notification preferences:', error);
            });
        },
    },
});
